/**
 * Whether one rendered frame moved a hull too far for a wake to be drawn.
 *
 * ## What this is not
 *
 * Not gameplay, not a teleport invented on the client, and not a tolerance. It
 * is the admission that no intermediate sample exists. A wake is drawn by
 * laying residue down ALONG a path; if the hull covered its own entire declared
 * trail length between two renders, there is no path to lay it along - only two
 * places and a straight line nobody ever saw the ship fly.
 *
 * ## Why the threshold is the band, and needs no new field
 *
 * The band was already the authored rule: `maxLengthHulls` is the furthest a
 * particle may sit from its ship, and it is now also the stretch the brightness
 * gradient runs over. Below a step of one band, a particle cannot go from birth
 * to cut in a single frame, so the gradient always gets at least one frame to
 * dim it - the bound is `base * step / band`. At or above one band, no fade
 * could help: a particle would be drawn at full brightness and be gone before
 * anything else was drawn. Dimming is the wrong answer there, and dropping the
 * residue is the right one.
 *
 * So the same number decides three things, and the descriptor gained nothing:
 * 1.6 hull diameters for Silver Hunter, 2.0 for Golden Wing and Cyber Rainbow.
 */
export interface TrailContinuityQuery {
  /** How far the hull was DRAWN from where it was drawn last frame, in world units. */
  readonly travelledWorldUnits: number;
  /** `maxLengthHulls * diameter`, or null for a skin with no trail. */
  readonly bandWorldUnits: number | null;
}

export function trailFrameIsDiscontinuous(query: TrailContinuityQuery): boolean {
  const { travelledWorldUnits, bandWorldUnits } = query;
  if (bandWorldUnits === null || !(bandWorldUnits > 0)) {
    // Nothing declares a length, so there is no wake to break.
    return false;
  }
  if (!Number.isFinite(travelledWorldUnits) || travelledWorldUnits < 0) {
    return false;
  }
  /*
   * At the band exactly, not past it. A step of one whole band would place a
   * newborn particle on the limit in the very next frame, drawn once at full
   * brightness and cut - which is the pop this rule exists to prevent.
   */
  return travelledWorldUnits >= bandWorldUnits;
}
